import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  cancelSubscriptionWithStripe,
  getCompleteSubscriptionData,
  resumeSubscriptionRenewal,
} from "../../services/subscription-payment-service";
import { useAuth } from "../../context/authContext";
import ActionModal from "../../components/UI/ActionModal/ActionModal";

import styles from "./SubscriptionManageInline.module.css";

function parseManageQuery(search) {
  const p = new URLSearchParams(search);
  const planId = p.get("id");
  const productName = p.get("product");
  const tier = p.get("tier");
  if (!planId || !productName || !tier) return null;
  return {
    planId,
    productName,
    tier,
    projectTier: p.get("projectTier"),
    status: p.get("status"),
    amount: p.get("amount"),
    currency: p.get("currency"),
    stripeId: p.get("stripeId"),
    autoRenew: p.get("autoRenew"),
  };
}

export default function SubscriptionManageInline() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const manageQuery = useMemo(
    () => parseManageQuery(location.search),
    [location.search]
  );

  const [managingPlan, setManagingPlan] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useLayoutEffect(() => {
    if (!manageQuery) {
      setManagingPlan(null);
      return;
    }

    const q = manageQuery;
    const fallback = {
      id: q.planId,
      product: q.productName,
      tier: q.tier,
      projectTier: q.projectTier || q.tier,
      status: q.status || undefined,
      amount: q.amount ?? undefined,
      currency: q.currency || undefined,
      stripeSubscriptionId: q.stripeId || undefined,
      autoRenew: q.autoRenew === "true",
      cancelAtPeriodEnd: false,
      renewalDate: undefined,
    };

    setManagingPlan(fallback);

    let cancelled = false;
    setIsLoadingPlan(true);
    (async () => {
      try {
        const response = await getCompleteSubscriptionData(q.planId);
        if (cancelled) return;
        if (response?.success && response?.data) {
          const d = response.data;
          setManagingPlan({
            id: d._id || d.id || fallback.id,
            planProjectId: d.projectId,
            product: d.product,
            tier: d.subscriptionPlan,
            projectTier: d.projectTier,
            status: d.status,
            amount: d.amount,
            currency: d.currency,
            stripeSubscriptionId: d.stripeSubscriptionId,
            autoRenew: d.autoRenew,
            renewalDate: d.renewalDate,
            cancelAtPeriodEnd: Boolean(d.cancelAtPeriodEnd),
          });
        }
      } catch {
        if (!cancelled) setManagingPlan(fallback);
      } finally {
        if (!cancelled) setIsLoadingPlan(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [manageQuery]);

  const clearManageQuery = useCallback(() => {
    navigate("/billing", { replace: true, state: { tab: "plans" } });
  }, [navigate]);

  const formatRenewalDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatMoney = (amount, currency) => {
    const cur = (currency || user?.currency || "RON").toUpperCase();
    const n = Number(amount);
    if (Number.isNaN(n)) return `— ${cur}`;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: cur,
      }).format(n);
    } catch {
      return `${n} ${cur}`;
    }
  };

  const cancelWithStaleRecovery = async (cancelImmediately) => {
    const planId = managingPlan?.id;
    let subId = managingPlan?.stripeSubscriptionId;
    const base = {
      reason: "User requested cancellation",
      feedback: "",
      cancelImmediately,
      existingPlanId: planId,
    };
    let response = await cancelSubscriptionWithStripe({
      ...base,
      subscriptionId: subId,
    });
    if (
      !response.success &&
      response.code === "STALE_SUBSCRIPTION_REFERENCE" &&
      planId
    ) {
      const refresh = await getCompleteSubscriptionData(planId);
      if (refresh?.success && refresh?.data?.stripeSubscriptionId) {
        const d = refresh.data;
        subId = d.stripeSubscriptionId;
        setManagingPlan((prev) =>
          prev
            ? {
                ...prev,
                stripeSubscriptionId: d.stripeSubscriptionId,
                status: d.status,
                autoRenew: d.autoRenew,
                cancelAtPeriodEnd: Boolean(d.cancelAtPeriodEnd),
                renewalDate: d.renewalDate,
              }
            : prev
        );
        response = await cancelSubscriptionWithStripe({
          ...base,
          subscriptionId: subId,
        });
      }
    }
    return response;
  };

  const handleCancelAtPeriodEnd = async () => {
    if (!managingPlan?.stripeSubscriptionId) return;
    setIsBusy(true);
    try {
      const response = await cancelWithStaleRecovery(false);
      if (!response.success) throw new Error(response.error || "cancel");
      clearManageQuery();
    } finally {
      setIsBusy(false);
    }
  };

  const handleCancelImmediately = async () => {
    if (!managingPlan?.stripeSubscriptionId) return;
    setIsBusy(true);
    try {
      const response = await cancelWithStaleRecovery(true);
      if (!response.success) throw new Error(response.error || "cancel");
      clearManageQuery();
    } finally {
      setIsBusy(false);
    }
  };

  const handleResumeRenewal = async () => {
    if (!managingPlan?.stripeSubscriptionId) return;
    setIsBusy(true);
    try {
      const response = await resumeSubscriptionRenewal({
        subscriptionId: managingPlan.stripeSubscriptionId,
      });
      if (!response.success) throw new Error(response.error || "resume");
      const refresh = await getCompleteSubscriptionData(managingPlan.id);
      if (refresh?.success && refresh?.data) {
        const d = refresh.data;
        setManagingPlan((prev) => ({
          ...prev,
          id: d._id || d.id || prev.id,
          planProjectId: d.projectId,
          status: d.status,
          stripeSubscriptionId: d.stripeSubscriptionId,
          autoRenew: d.autoRenew,
          cancelAtPeriodEnd: Boolean(d.cancelAtPeriodEnd),
          renewalDate: d.renewalDate,
        }));
      } else {
        setManagingPlan((prev) => ({
          ...prev,
          cancelAtPeriodEnd: false,
          autoRenew: true,
        }));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const modalVariant =
    confirmAction === "cancelImmediately"
      ? "danger"
      : confirmAction === "cancelAtPeriodEnd" || confirmAction === "resumeRenewal"
        ? "warning"
        : "default";

  const confirmModalCopy = confirmAction
    ? {
        title: t(`billingPage.subscriptionManage.confirmModals.${confirmAction}.title`),
        message: t(
          `billingPage.subscriptionManage.confirmModals.${confirmAction}.message`,
          confirmAction === "cancelAtPeriodEnd"
            ? { renewalDate: formatRenewalDate(managingPlan?.renewalDate) }
            : {}
        ),
        confirm: t(
          `billingPage.subscriptionManage.confirmModals.${confirmAction}.confirm`
        ),
      }
    : null;

  const handleConfirmModal = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    switch (action) {
      case "resumeRenewal":
        await handleResumeRenewal();
        break;
      case "cancelAtPeriodEnd":
        await handleCancelAtPeriodEnd();
        break;
      case "cancelImmediately":
        await handleCancelImmediately();
        break;
      default:
        break;
    }
  };

  if (!manageQuery || !managingPlan) return null;

  const statusNorm = String(managingPlan?.status || "").toLowerCase();
  const isActive = statusNorm === "active";
  const canUseStripeSubscription =
    isActive && Boolean(managingPlan?.stripeSubscriptionId);

  const statusPillClass =
    statusNorm === "active"
      ? styles.statusPillActive
      : ["inactive", "expired", "canceled", "canceling"].includes(statusNorm)
        ? styles.statusPillDanger
        : statusNorm === "pending"
          ? styles.statusPillWarn
          : styles.statusPillNeutral;

  return (
    <section className={styles.wrap} aria-label="Subscription management">
      <div className={styles.headerRow}>
        <button type="button" className={styles.backBtn} onClick={clearManageQuery}>
          ← {t("billingPage.subscriptionManage.back")}
        </button>
        <div className={styles.headerText}>
          <div className={styles.eyebrow}>
            {t("billingPage.subscriptionManage.title")}
          </div>
          <div className={styles.product}>{managingPlan.product || "—"}</div>
        </div>
        <div className={styles.rightMeta}>
          {isLoadingPlan && (
            <span className={styles.syncing}>{t("billingPage.subscriptionManage.loading")}</span>
          )}
          <span className={`${styles.statusPill} ${statusPillClass}`}>
            {managingPlan.status || "—"}
          </span>
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            {t("billingPage.subscriptionManage.summaryLabel")}
          </div>
          <div className={styles.priceRow}>
            <div className={styles.price}>
              {formatMoney(managingPlan.amount, managingPlan.currency)}
            </div>
            <div className={styles.cadence}>
              {t("billingPage.subscriptionManage.amountCadence")}
            </div>
          </div>
          <dl className={styles.detailList}>
            <dt className={styles.detailDt}>
              {t("billingPage.subscriptionManage.tier")}
            </dt>
            <dd className={styles.detailDd}>
              {managingPlan.tier || managingPlan.projectTier || "—"}
            </dd>
            <dt className={styles.detailDt}>
              {t("billingPage.subscriptionManage.renewal")}
            </dt>
            <dd className={styles.detailDd}>
              {formatRenewalDate(managingPlan.renewalDate)}
            </dd>
            <dt className={styles.detailDt}>
              {t("billingPage.subscriptionManage.autoRenew")}
            </dt>
            <dd className={styles.detailDd}>
              {managingPlan.autoRenew
                ? t("billingPage.managePlans.autoRenewOn")
                : t("billingPage.managePlans.autoRenewOff")}
            </dd>
          </dl>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>
            {t("billingPage.subscriptionManage.subscriptionTitle")}
          </div>
          <p className={styles.cardLead}>
            {t("billingPage.subscriptionManage.subscriptionLead")}
          </p>
          <div className={styles.actionStack}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => setConfirmAction("resumeRenewal")}
              disabled={
                isLoadingPlan ||
                isBusy ||
                !canUseStripeSubscription ||
                !managingPlan.cancelAtPeriodEnd
              }
            >
              {t("billingPage.subscriptionManage.resumeRenewal")}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => setConfirmAction("cancelAtPeriodEnd")}
              disabled={
                isLoadingPlan ||
                isBusy ||
                !canUseStripeSubscription ||
                managingPlan.cancelAtPeriodEnd
              }
            >
              {t("billingPage.subscriptionManage.cancelAtPeriodEnd")}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnDanger}`}
              onClick={() => setConfirmAction("cancelImmediately")}
              disabled={isLoadingPlan || isBusy || !canUseStripeSubscription}
            >
              {t("billingPage.subscriptionManage.cancelImmediately")}
            </button>
          </div>
          <p className={styles.stripeFoot}>
            {t("billingPage.subscriptionManage.stripeNote")}
          </p>
        </div>
      </div>

      <ActionModal
        isOpen={Boolean(confirmAction)}
        onClose={() => !isBusy && setConfirmAction(null)}
        onConfirm={handleConfirmModal}
        title={confirmModalCopy?.title ?? ""}
        message={confirmModalCopy?.message ?? ""}
        confirmText={confirmModalCopy?.confirm ?? ""}
        cancelText={t("cancel")}
        type={modalVariant}
        isLoading={isBusy}
      />
    </section>
  );
}

